import { h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import http from "../../../../../foundation/api/http";
import Cookies from "../../../../../foundation/cookies";
import ClientError from "../../../../../foundation/errors/clientError";
import ServerError from "../../../../../foundation/errors/serverError";
import { handleError } from "../../../../../foundation/errors/errors";
import { urlResolve } from "../../../../../foundation/utils/url";
import getRedirectUrl from "../../../../../foundation/utils/get-redirect-url";
import { performLogin } from "../../../../../foundation/utils/saml";

import LoginParams from "../../types/LoginParams";
import FormFieldStatus from "../../types/FormFieldStatus";

import LoadingOverlay from "../../../../../library/LoadingOverlay";
import TextFormField from "../../../../../library/FormField/TextFormField";
import Button from "../../../../../library/Button";
import TipText from "../TipText/TipText";

import "./style.css";

interface Props {
    loginParams?: LoginParams;
}

function LoginSSOForm({
    loginParams
}: Props) {
    const [email, setEmail] = useState(loginParams?.email || "");
    const [emailStatus, setEmailStatus] = useState<FormFieldStatus>({ type: "empty" });
    const [loading, setLoading] = useState<boolean>(false);
    const emailElement = useRef<HTMLInputElement>(null);
    const emailTimeoutRef = useRef<number>();

    useEffect(() => {
        if (email) {
            checkEmailStatus(email);
        }
    }, []);

    function handleEmailChange(event: Event) {
        const { value } = event.target as HTMLInputElement;
        setEmail(value);
        clearTimeout(emailTimeoutRef.current);
        emailTimeoutRef.current = window.setTimeout(() => checkEmailStatus(value), 1000);
    }

    function checkEmailStatus(value: string) {
        if (!value && emailStatus.type !== "empty") {
            setEmailStatus({ type: "empty" });
            return;
        }

        if (!emailElement.current!.checkValidity()) {
            setEmailStatus({
                type: "error",
                explanation: /@/.test(value) ? "Your email doesn't look right!" : ""
            });
            return;
        }

        setEmailStatus({ type: "success" });
    }

    async function handleLogin(event: Event) {
        event.preventDefault();

        const redirectToFromSession = sessionStorage.getItem("redirectTo");
        if (redirectToFromSession) {
            Cookies.set("redirect", getRedirectUrl(redirectToFromSession, "/projects"));
            sessionStorage.removeItem("redirectTo");
        }

        if (!isFormValid()) {
            return;
        }

        setLoading(true);

        try {
            await performLogin(email, { useExtensionsWindow: loginParams?.source === "extensions" });
            setLoading(false);
        } catch (error) {
            const zplError = error as (ClientError | ServerError);

            if (redirectToFromSession) {
                sessionStorage.setItem("redirectTo", getRedirectUrl(redirectToFromSession, "/"));
            }

            setLoading(false);
            switch (zplError.status) {
                case http.STATUS.NOT_FOUND: // No such user
                    setEmailStatus({
                        type: "error",
                        explanation: (
                            <span>
                                <strong>{email}</strong> is not configured for SSO. Try another email or <a href={urlResolve("login")}>other login options</a>.
                            </span>
                        )
                    });
                    break;
                case http.STATUS.PRECONDITION_FAILED: // User not allowed
                    handleError(zplError, {
                        dialogCallback: () => location.assign(urlResolve("login", { query: { email } }))
                    });
                    break;
                default:
                    handleError(zplError);
                    break;
            }
        }
    }

    function isFormValid() {
        return !["error", "warning", "empty"].includes(emailStatus.type);
    }

    return (
        <form class="loginSSOForm" onSubmit={handleLogin}>
            {loading && <LoadingOverlay/>}
            <TextFormField
                class="loginFormField"
                name="Email"
                value={email}
                placeholder="Work email"
                nativeProps={{
                    class: "loginSSOFormInput",
                    autocapitalize: "off",
                    autocomplete: "email",
                    type: "email",
                    required: true,
                    ref: emailElement,
                    onInput: handleEmailChange
                }}
                maxLength={99}
                shouldDisplayCountDown={false}
                shouldAlwaysDisplayTipText={true}
                tipText={<TipText type={emailStatus.type} explanation={emailStatus.explanation}/>}
                autoFocus/>
            <Button
                disabled={!isFormValid()}
                class="loginSSOFormButton"
                type="submit"
                primary>
                Login with SSO
            </Button>
            <div class="loginWithEmailContainer">
                <a
                    href={urlResolve("login", { query: { redirect: loginParams?.redirect } })}>
                    Login with Email
                </a>
            </div>
        </form>
    );
}

export default LoginSSOForm;
